/**
 * `/parent/profile` — the Parent Portal profile view (P-SCH-V1, ADR-0103).
 *
 * The guardian's own read-only profile (name · email · coaching centre) plus
 * the list of students they're linked to, with each child's relationship.
 * Strictly read-only — a guardian's account details are managed by the coaching
 * centre, not self-served here (there's no edit affordance). Sourced from
 * `auth.getCurrentUser` + `learning.learner.guardian.children`.
 */
import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { GraduationCap, Mail, School, User } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Skeleton,
  StatusPill,
} from '@mangrove/ui';
import { trpc } from '../lib/trpc.js';
import { useCurrentUser } from '../lib/auth.js';
import { ParentRoute } from '../components/parent-portal/parent-shell.js';
import {
  childInitials,
  childLabel,
  relationshipLabel,
} from '../components/parent-portal/child-selector.js';
import { ErrorCard, ParentHero } from '../components/parent-portal/parent-shared.js';

export const Route = createFileRoute('/parent/profile')({
  component: ParentProfilePage,
});

function ParentProfilePage(): React.JSX.Element {
  return (
    <ParentRoute title="Profile">
      <ParentProfileContent />
    </ParentRoute>
  );
}

function ParentProfileContent(): React.JSX.Element {
  const me = useCurrentUser();
  const childrenQ = trpc.learning.learner.guardian.children.useQuery();
  const children = childrenQ.data?.children ?? [];

  return (
    <div className="space-y-6">
      <ParentHero
        title="Your profile"
        subtitle="Your account details and the students linked to you. These are managed by your coaching centre."
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="card-soft border-l-2 border-l-primary/40">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="flex size-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                <User className="size-4" aria-hidden="true" />
              </span>
              Account
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {me.isPending ? (
              <div className="space-y-3">
                <Skeleton className="h-5 w-2/3" />
                <Skeleton className="h-5 w-1/2" />
              </div>
            ) : me.data ? (
              <dl className="space-y-3 text-sm">
                <Field icon={User} label="Name" value={me.data.name ?? '—'} />
                <Field icon={Mail} label="Email" value={me.data.email ?? '—'} />
                <Field icon={School} label="Coaching centre" value={me.data.tenantName ?? '—'} />
              </dl>
            ) : (
              <p className="text-sm text-muted-foreground">Your account isn’t available.</p>
            )}
          </CardContent>
        </Card>

        <Card className="card-soft border-l-2 border-l-primary/40">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="flex size-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                <GraduationCap className="size-4" aria-hidden="true" />
              </span>
              Linked students
            </CardTitle>
          </CardHeader>
          <CardContent>
            {childrenQ.isPending ? (
              <div className="space-y-3">
                {Array.from({ length: 2 }, (_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : childrenQ.isError ? (
              <ErrorCard
                message="We couldn’t load your linked students."
                onRetry={() => void childrenQ.refetch()}
              />
            ) : children.length === 0 ? (
              <EmptyState
                icon={GraduationCap}
                title="No linked students"
                description="When the coaching centre links a student to your account, they’ll appear here."
              />
            ) : (
              <ul className="divide-y">
                {children.map((child) => (
                  <li
                    key={child.studentUserId}
                    className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
                  >
                    <span
                      aria-hidden="true"
                      className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-sm font-semibold text-primary"
                    >
                      {childInitials(child)}
                    </span>
                    <span className="min-w-0 flex-1 truncate font-medium text-foreground">
                      {childLabel(child)}
                    </span>
                    <StatusPill
                      label={relationshipLabel(child.relationship)}
                      tone="neutral"
                      size="sm"
                    />
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Field({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof User;
  label: string;
  value: string;
}): React.JSX.Element {
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <div className="min-w-0">
        <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
        <dd className="truncate font-medium text-foreground">{value}</dd>
      </div>
    </div>
  );
}
